'use client'

import { useTranslation } from '@shared/config/i18n'

import { Button, VStack } from '@podo-app/design-system-temp'

import { NPS_NEGATIVE_REASON_TAGS, NPS_POSITIVE_REASON_TAGS } from './nps-survey.constants'

type ReasonVariant = 'positive' | 'negative'

const VARIANT_CONFIG = {
  positive: {
    titleKey: 'lessonReview.nps.reason.positive.title',
    descriptionKeys: [
      'lessonReview.nps.reason.positive.descriptionLine1',
      'lessonReview.nps.reason.positive.descriptionLine2',
    ],
    tags: NPS_POSITIVE_REASON_TAGS,
  },
  negative: {
    titleKey: 'lessonReview.nps.reason.negative.title',
    descriptionKeys: [
      'lessonReview.nps.reason.negative.descriptionLine1',
      'lessonReview.nps.reason.negative.descriptionLine2',
    ],
    tags: NPS_NEGATIVE_REASON_TAGS,
  },
} as const

interface NpsReasonStepProps {
  variant: ReasonVariant
  selectedReasons: string[]
  reasonText: string
  onToggleReason: (reason: string) => void
  onChangeReasonText: (text: string) => void
  onSubmit: () => void
  onSkip: () => void
}

export const NpsReasonStep = ({
  variant,
  selectedReasons,
  reasonText,
  onToggleReason,
  onChangeReasonText,
  onSubmit,
  onSkip,
}: NpsReasonStepProps) => {
  const t = useTranslation()
  const config = VARIANT_CONFIG[variant]
  const showTextarea = selectedReasons.includes('other')
  const hasValidSelection = selectedReasons.length > 0 && (!showTextarea || reasonText.trim().length > 0)

  return (
    <VStack className="h-dvh" data-testid="nps-reason-step">
      <VStack className="flex-1 gap-10 items-center justify-center overflow-y-auto overflow-x-hidden scrollbar-hide [&::-webkit-scrollbar]:hidden p-5">
        <VStack className="gap-2 w-full text-center">
          <span className="text-[20px] font-bold leading-7 tracking-[-0.4px] text-[#1C1C1C]">{t(config.titleKey)}</span>
          <div className="flex flex-col">
            {config.descriptionKeys.map((descriptionKey) => (
              <span key={descriptionKey} className="text-[14px] font-medium leading-5 text-[#757575]">
                {t(descriptionKey)}
              </span>
            ))}
          </div>
        </VStack>

        <div className="flex flex-col gap-5 w-full">
          <div className="flex flex-wrap gap-2 items-start justify-center max-w-[335px] w-full mx-auto">
            {config.tags.map((tag) => (
              <button
                key={tag.value}
                type="button"
                className={`shrink-0 h-11 px-3 py-1.5 rounded-lg border border-solid cursor-pointer ${
                  selectedReasons.includes(tag.value) ? 'bg-[#f2fcec] border-[#6abe36]' : 'bg-white border-[#e8e8e8]'
                }`}
                onClick={() => onToggleReason(tag.value)}
                data-testid={`chip-reason-${tag.value}`}
              >
                <span className="text-[14px] font-medium text-[#1C1C1C] whitespace-nowrap leading-5">
                  {t(`lessonReview.nps.reason.tags.${variant}.${tag.value}`)}
                </span>
              </button>
            ))}
          </div>

          {showTextarea && (
            <textarea
              className="w-full h-[84px] p-4 rounded-lg border border-[#E7E7E7] text-[16px] leading-6 resize-none outline-none focus:border-[#1C1C1C] placeholder:text-[#A5A5A5]"
              placeholder={t('lessonReview.nps.reason.placeholder')}
              value={reasonText}
              onChange={(e) => onChangeReasonText(e.target.value)}
              data-testid="textarea-nps-reason"
            />
          )}
        </div>
      </VStack>

      <VStack className="w-full shrink-0 gap-2 px-5 py-2">
        <Button size="xLarge" className="w-full" disabled={!hasValidSelection} onClick={onSubmit}>
          {t('common.actions.submit')}
        </Button>
        <button type="button" className="bg-transparent border-none cursor-pointer py-3" onClick={onSkip}>
          <span className="text-[14px] font-medium leading-5 text-[#A5A5A5]">
            {t('lessonReview.nps.actions.skipFeedback')}
          </span>
        </button>
      </VStack>
    </VStack>
  )
}
